/**
 * The DOM rendering of the event creator — form controls, preview, and delivery.
 *
 * All behaviour comes from `useEventCreator`; this file owns markup only, so the
 * embed, the hosted site, and any third-party host share identical form logic.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  LIMITS,
  type EventDraft,
  type EventFormatId,
  type EventRecurrence,
  type EventShowAs,
  type RecurrenceFrequency,
} from '@project/event-format';
import {
  type EventExport,
  type EventCreatorState,
  isRecord,
  durationPartsToMinutes,
  eventDurationMinutes,
  listTimeZones,
  sortTimeZonesByOffset,
  splitDurationMinutes,
  timeZonePlaceLabel,
  timeZoneOptionLabel,
  timeZoneOffsetLabel,
  useEventCreator,
} from '@project/event-creator';

import { DescribeEvent, type ProposeDraft } from './DescribeEvent';
import { fill, mergeMessages, plural, problemMessage, type EventCreatorMessages } from './messages';
import { eventRecap, previewDurationMinutes } from './recap';
import { copyExport, deliverExport, localTimeZone, nextHalfHour, todayLocal } from './download';
import { ensureEventCreatorStyles } from './styles';
import { TimeZonePicker, type TimeZoneChoice } from './TimeZonePicker';

export interface EventCreatorProps {
  /** Start from an existing event — an edit, a host prefill, or an AI-proposed draft. */
  readonly initialDraft?: EventDraft;
  readonly initialSession?: unknown;
  readonly onSessionChange?: (state: EventCreatorState) => void;
  readonly defaultDate?: string;
  readonly defaultTimeZone?: string;
  readonly defaultFormat?: EventFormatId;
  /** Hide the live format preview (embeds are often tight on space). */
  readonly showPreview?: boolean;
  /** Make collection the primary action and tuck individual exports behind a disclosure. */
  readonly collectionMode?: boolean;
  /** Inline editing: save/cancel only, with exports left to the host's saved event list. */
  readonly onCancelEditing?: () => void;
  readonly cancelEditingLabel?: string;
  /** Keep format selection and copying behind a disclosure for a simpler first-use flow. */
  readonly simpleExport?: boolean;
  /** Host-specific guidance for opening the downloaded file in a calendar app. */
  readonly downloadHelp?: string;
  /**
   * Collect the event somewhere instead of downloading it — a host that gathers several before
   * writing one file. Supplying this shows a button beside Download; without it, nothing changes.
   *
   * Deliberately **not** gated on validation, where Download is. A half-filled event handed to a
   * list is a row the user can see and correct; a half-filled event handed to a calendar is a bad
   * event. The gate belongs at the file, which is where `requestExport` already puts it.
   */
  /** Observe edits for a host-owned live calendar preview. */
  readonly onDraftChange?: (draft: EventDraft) => void;
  readonly onAdd?: (draft: EventDraft) => void;
  /** What that button says. Hosts phrase it themselves: adding and saving an edit are not the same act. */
  readonly addLabel?: string;
  /** Called after a successful export, before the file is delivered. */
  readonly onExport?: (payload: EventExport) => void;
  /** Skip the built-in file delivery so a host can deliver the export its own way. */
  readonly deliver?: (payload: EventExport) => void;
  /**
   * Turn free text into a draft. Supplying it shows the "describe it" box; the host owns the
   * transport, so this package never holds a model, a key, or an endpoint.
   */
  readonly propose?: ProposeDraft;
  /**
   * The language to name places in, and to pick plural forms with — a BCP 47 tag such as `de` or
   * `pt-BR`. Defaults to English, to match this component's own labels, rather than to the
   * browser's setting: a host that has not translated its page around the form does not want half
   * of it answering in Portuguese. The date and time pickers, and the offsets beside them, follow
   * the reader's own locale as they always have.
   */
  readonly locale?: string;
  /**
   * The component's own words, for a host that has translated its page. Partial: anything absent
   * stays English, so a catalogue that predates a new field renders that field in English rather
   * than blank. See `messages.ts` — `locale` alone does not translate the interface, because the
   * two are genuinely separate choices: a German page may well want its zone list in German while
   * a Swiss one wants French labels with German place names.
   */
  readonly messages?: Partial<EventCreatorMessages>;
}

/**
 * The alert ladder from Apple Calendar, in minutes before the start. "At time of event" is 0 —
 * the chip set this replaced could not say it at all, and it is the one people reach for on a
 * reminder-style event.
 */
const ALERT_CHOICES = [0, 5, 10, 15, 30, 60, 120, 1440, 2880, 10080] as const;
const FREQUENCIES: readonly RecurrenceFrequency[] = ['daily', 'weekly', 'monthly', 'yearly'];

/**
 * Apple's repeat menu, which puts the common intervals in the list instead of behind a number
 * field: "Every 2 Weeks" is one tap, and anything else goes to Custom. Same rules underneath —
 * a preset is just a frequency and an interval.
 */
/** The four frequencies as the custom menu names them. */
const FREQUENCY_MESSAGE: Record<RecurrenceFrequency, (m: EventCreatorMessages) => string> = {
  daily: (m) => m.frequencyDaily,
  weekly: (m) => m.frequencyWeekly,
  monthly: (m) => m.frequencyMonthly,
  yearly: (m) => m.frequencyYearly,
};

const REPEAT_PRESETS = [
  { id: 'never', message: 'repeatNever' },
  { id: 'daily', message: 'repeatDaily', frequency: 'daily', interval: 1 },
  { id: 'weekly', message: 'repeatWeekly', frequency: 'weekly', interval: 1 },
  { id: 'biweekly', message: 'repeatBiweekly', frequency: 'weekly', interval: 2 },
  { id: 'monthly', message: 'repeatMonthly', frequency: 'monthly', interval: 1 },
  { id: 'yearly', message: 'repeatYearly', frequency: 'yearly', interval: 1 },
  { id: 'custom', message: 'repeatCustom' },
] as const;

/** The label a frequency's Every-N unit takes, in the form its count calls for. */
function intervalUnit(
  frequency: RecurrenceFrequency,
  count: number,
  m: EventCreatorMessages,
  locale: string | undefined,
): string {
  const forms = {
    daily: m.unitDays,
    weekly: m.unitWeeks,
    monthly: m.unitMonths,
    yearly: m.unitYears,
  };
  return plural(forms[frequency], count, locale);
}

function alertLabel(minutes: number, m: EventCreatorMessages, locale: string | undefined): string {
  if (minutes === 0) return m.alertAtTime;
  if (minutes < 60) return plural(m.alertMinutesBefore, minutes, locale);
  if (minutes < 1440) return plural(m.alertHoursBefore, minutes / 60, locale);
  if (minutes < 10080) return plural(m.alertDaysBefore, minutes / 1440, locale);
  return plural(m.alertWeeksBefore, minutes / 10080, locale);
}

/** "Alert", then "Second alert" — Apple's wording, and a sensible fallback past its two. */
const alertRowLabel = (index: number, m: EventCreatorMessages): string =>
  m.alertRowLabels[index] ?? fill(m.alertRowFallback, { index: index + 1 });

/** Which entry in the repeat menu a rule corresponds to, or 'custom' when none of them fit. */
/**
 * A group of optional fields, closed until it is wanted.
 *
 * Not every field has to be on screen at once. Five of the form's groups are things most events
 * never carry — nobody invites anyone to "dentist", and the alerts a calendar applies by default
 * are usually right — and on a phone they are the difference between a form that fits the screen
 * and one you scroll past to reach the button. So they collapse.
 *
 * Two rules keep collapsing from hiding anything:
 *   - A group that already holds something opens. That covers a draft the funnel read out of a
 *     flyer, or an event opened from the list to be corrected: nothing arrives folded away.
 *   - A closed group says what is in it ("2 invited"), so the fold is never a place where work
 *     silently disappeared.
 *
 * `<details>` rather than a button and a state flag: it is a disclosure the browser already
 * knows how to announce, it opens for find-in-page in every modern engine, and it needs no
 * JavaScript to work — which is the same reason the rest of this component is plain controls.
 */
function Optional({
  name,
  tally,
  problem = false,
  open,
  onToggle,
  children,
}: {
  readonly name: string;
  /** What the group holds, when it holds anything — the summary line, and the reason it opens. */
  readonly tally?: string;
  /** A refused download left a problem in here. It opens, and stays open, whatever anyone chose. */
  readonly problem?: boolean;
  /** The reader's own choice, once they have made one; `undefined` until they touch the fold. */
  readonly open?: boolean;
  readonly onToggle: (open: boolean) => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  // Derived rather than remembered from the first render: a draft can arrive at any time — the
  // "describe it" box loads one into a form that is already on screen — and a group that filled
  // up after mount has to open too, not just one that was full when the component appeared.
  const shown = problem || (open ?? tally !== undefined);
  // A <details> the reader can also toggle is not something React's diff can be trusted with: it
  // compares against what it last *rendered*, not against the element, so once a click has moved
  // the DOM out from under it a re-render that computes the same value as before leaves the
  // element where the click left it. That is the difference between a refused download opening
  // the group holding its reason and one that silently stays folded. So the element is told, on
  // every render, what it is.
  const element = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (element.current !== null && element.current.open !== shown) element.current.open = shown;
  });
  return (
    <details
      ref={element}
      className="ec-group ec-optional"
      open={shown}
      onToggle={(event) => {
        // Only a real toggle is a choice. React setting `open` to match `shown` fires this event
        // too, and recording that as the reader's preference would freeze the fold on mount.
        //
        // Nor is a click on a group that is being held open by a problem: it cannot close, so
        // "closed" is not what the reader asked for, and storing it would spring shut the moment
        // they fixed the field — hiding the input they were typing in, at the keystroke that made
        // it valid.
        if (problem) return;
        if (event.currentTarget.open !== shown) onToggle(event.currentTarget.open);
      }}
    >
      <summary>
        {name}
        {tally !== undefined && <span className="ec-tally">{tally}</span>}
      </summary>
      <div className="ec-group-body">{children}</div>
    </details>
  );
}

function presetFor(recurrence: EventRecurrence | undefined): string {
  if (recurrence === undefined) return 'never';
  const interval = recurrence.interval ?? 1;
  const match = REPEAT_PRESETS.find(
    (preset) =>
      'frequency' in preset &&
      preset.frequency === recurrence.frequency &&
      preset.interval === interval,
  );
  return match?.id ?? 'custom';
}

/**
 * The duration boxes, largest first. An all-day event shows Days alone — the other two have no
 * meaning for it, since the file it produces carries dates and no clock.
 */
const DURATION_UNITS = [
  { key: 'days', message: 'durationDays' },
  { key: 'hours', message: 'durationHours' },
  { key: 'minutes', message: 'durationMinutes' },
] as const;

type DurationUnit = (typeof DURATION_UNITS)[number]['key'];
type DurationFields = Record<DurationUnit, string>;

/** The draft's duration as field contents; empty boxes when there is no end to measure to. */
function asFields(minutes: number | null): DurationFields {
  if (minutes === null) return { days: '', hours: '', minutes: '' };
  const parts = splitDurationMinutes(minutes);
  return {
    days: String(parts.days),
    hours: String(parts.hours),
    minutes: String(parts.minutes),
  };
}

/** A repeat runs forever, a set number of times, or up to a date — never two of those. */
type RepeatEnd = 'never' | 'count' | 'until';

function withoutEnd(recurrence: EventRecurrence): EventRecurrence {
  return {
    frequency: recurrence.frequency,
    ...(recurrence.interval === undefined ? {} : { interval: recurrence.interval }),
  };
}

/** Says the rule back in a sentence: nobody should have to read RRULE to check their event. */
function repeatSummary(
  recurrence: EventRecurrence,
  m: EventCreatorMessages,
  locale: string | undefined,
): string {
  const interval = recurrence.interval ?? 1;
  const every =
    interval === 1
      ? {
          daily: m.repeatDaily,
          weekly: m.repeatWeekly,
          monthly: m.repeatMonthly,
          yearly: m.repeatYearly,
        }[recurrence.frequency]
      : `${m.every} ${interval} ${intervalUnit(recurrence.frequency, interval, m, locale)}`;
  if (recurrence.count !== undefined)
    return fill(m.repeatSummaryCount, { every, count: recurrence.count });
  if (recurrence.until !== undefined && recurrence.until !== '')
    return fill(m.repeatSummaryUntil, { every, date: recurrence.until });
  return fill(m.repeatSummaryForever, { every });
}

export function EventCreator(props: EventCreatorProps): React.JSX.Element {
  const { locale, showPreview = true } = props;
  const collecting = props.collectionMode === true && props.onAdd !== undefined;
  const ExportContainer = collecting ? 'details' : 'div';
  const SettingsContainer = props.simpleExport ? 'details' : 'div';
  const m = mergeMessages(props.messages);
  useEffect(() => ensureEventCreatorStyles(), []);

  // Only today gets the next half hour. On any other date there is no "now" to be later than,
  // and 09:00 is the better guess for a thing being planned.
  const openingDate = props.defaultDate ?? todayLocal();
  const creator = useEventCreator({
    initialSession: props.initialSession,
    defaults: {
      date: openingDate,
      ...(openingDate === todayLocal() ? { clock: nextHalfHour() } : {}),
      // No zone unless the host names one. A zone is a claim about the event, not about the
      // person filling the form: "9am" typed on a laptop in Berlin usually means 9am wherever
      // the event happens, and a zone-less event does exactly that in every calendar. The
      // browser's zone is still one tap away — it's the first option in the picker.
      ...(props.defaultTimeZone === undefined ? {} : { timeZone: props.defaultTimeZone }),
      ...(props.defaultFormat === undefined ? {} : { formatId: props.defaultFormat }),
    },
    ...(props.initialDraft === undefined ? {} : { initialDraft: props.initialDraft }),
  });

  const { draft, dispatch, problemFor } = creator;
  const onDraftChange = props.onDraftChange;
  useEffect(() => {
    onDraftChange?.(draft);
  }, [draft, onDraftChange]);
  const browserZone = useMemo(() => localTimeZone(), []);
  const allDay = draft.start.kind === 'date';
  const recurrence = draft.recurrence;
  const repeatEnd: RepeatEnd =
    recurrence?.count !== undefined ? 'count' : recurrence?.until !== undefined ? 'until' : 'never';
  const setRecurrence = (value: EventRecurrence | undefined): void =>
    dispatch({ type: 'set-recurrence', value });
  const sessionExtras = isRecord(props.initialSession) ? props.initialSession : {};
  const [attendee, setAttendee] = useState(
    typeof sessionExtras.pendingInvitee === 'string' ? sessionExtras.pendingInvitee : '',
  );
  const [copied, setCopied] = useState(false);
  // Sticky, because a custom rule can happen to match a preset — "every 1 week" is a weekly
  // event — and the custom controls disappearing mid-edit would be baffling.
  const [customRepeat, setCustomRepeat] = useState(false);
  const repeatPreset = customRepeat && recurrence !== undefined ? 'custom' : presetFor(recurrence);
  const alerts = draft.remindersMinutesBefore ?? [];
  // One row per alert, plus an empty one to add the next — so the first render shows a single
  // "Alert" row and a second appears only once the first is set, the way Apple's sheet does.
  const alertRows = alerts.length < LIMITS.reminders ? [...alerts, undefined] : [...alerts];

  const guests = draft.attendees ?? [];
  /**
   * Which optional groups the reader has opened or shut by hand. A name that is absent has not
   * been touched, and its group follows what it holds; loading a whole new draft clears the map,
   * because the choices were about the last event, not this one.
   */
  const [folds, setFolds] = useState<Record<string, boolean>>((): Record<string, boolean> =>
    attendee ? { invitees: true } : {},
  );
  /**
   * `key` is what the reader's choice is remembered under and `name` is what the group is called.
   * They were one string until the labels became translatable, at which point sharing them would
   * have meant a group folding itself back up the moment its own name changed language.
   */
  const fold = (
    key: string,
    name: string,
    tally: string | undefined,
    problem: boolean,
  ): {
    readonly name: string;
    readonly tally?: string;
    readonly problem: boolean;
    readonly open?: boolean;
    readonly onToggle: (open: boolean) => void;
  } => ({
    name,
    ...(tally === undefined ? {} : { tally }),
    problem,
    ...(folds[key] === undefined ? {} : { open: folds[key] }),
    onToggle: (open) => setFolds((state) => ({ ...state, [key]: open })),
  });
  /**
   * Whether a refused download left a problem inside a group. A form that says nothing and does
   * nothing is the worst outcome here: `submit` returns without exporting when validation fails,
   * so the reason has to be on screen, and a folded group is not on screen.
   */
  const holdsProblem = (...fields: readonly string[]): boolean =>
    creator.problems.some((issue) => fields.some((field) => issue.field.startsWith(field)));
  /**
   * What a collapsed group is holding, or `undefined` when it is holding nothing — which is also
   * what decides whether it opens (see `Optional`).
   */
  const tally = (...parts: readonly (string | false)[]): string | undefined => {
    const held = parts.filter((part): part is string => part !== false);
    return held.length === 0 ? undefined : held.join(' · ');
  };
  const endZone = draft.end?.kind === 'dateTime' ? draft.end.timeZone : undefined;
  // Sticky like the custom repeat: choosing the same zone on both sides must not fold the second
  // control away underneath someone who is still choosing.
  const [splitZonesChosen, setSplitZones] = useState(false);
  const splitZones =
    splitZonesChosen || (endZone !== undefined && endZone !== creator.state.timeZone);

  const startDate =
    draft.start.kind === 'date' ? draft.start.date : draft.start.dateTime.slice(0, 10);
  const startTime = draft.start.kind === 'date' ? '' : draft.start.dateTime.slice(11, 16);
  const end = draft.end;
  const endDate =
    end === undefined ? '' : end.kind === 'date' ? end.date : end.dateTime.slice(0, 10);
  const endTime = end === undefined || end.kind === 'date' ? '' : end.dateTime.slice(11, 16);

  // The picker displays a city/country string but keeps the IANA identifier as its value. Invalid
  // draft times use the stable city-name fallback order from the shared helper.
  const zoneOptions = useMemo(() => {
    const zones = [
      ...new Set([
        ...(browserZone === undefined ? [] : [browserZone]),
        'UTC',
        ...listTimeZones(),
        ...(creator.state.timeZone === undefined ? [] : [creator.state.timeZone]),
      ]),
    ];
    return sortTimeZonesByOffset(zones, `${startDate}T${startTime}`);
  }, [browserZone, creator.state.timeZone, startDate, startTime]);
  const zoneChoices = useMemo<readonly TimeZoneChoice[]>(
    () =>
      zoneOptions.map((zone) => ({
        zone,
        displayLabel: timeZonePlaceLabel(zone, locale),
        optionLabel: timeZoneOptionLabel(zone, `${startDate}T${startTime}`, locale),
      })),
    [locale, startDate, startTime, zoneOptions],
  );
  const startOffset =
    !allDay && creator.state.timeZone !== undefined
      ? timeZoneOffsetLabel(creator.state.timeZone, `${startDate}T${startTime}`)
      : undefined;
  const endOffset =
    !allDay && endZone !== undefined && endDate !== '' && endTime !== ''
      ? timeZoneOffsetLabel(endZone, `${endDate}T${endTime}`)
      : undefined;

  // The duration is the draft's, not a second copy of it — it recomputes whenever either end
  // moves. The typed values exist only while someone is filling the boxes in: the draft's own
  // split carries (26 hours becomes 1 day, 2 hours), and doing that mid-keystroke would move the
  // number out from under the cursor. All three are held together, or the carry would land in
  // Days while Hours still showed the 26 that caused it.
  const duration = eventDurationMinutes(draft);
  const previewDuration = previewDurationMinutes(draft);
  const [typedDuration, setTypedDuration] = useState<DurationFields | null>(() => {
    const pending = sessionExtras.pendingDuration;
    return isRecord(pending) &&
      typeof pending.days === 'string' &&
      typeof pending.hours === 'string' &&
      typeof pending.minutes === 'string'
      ? { days: pending.days, hours: pending.hours, minutes: pending.minutes }
      : null;
  });
  const onSessionChange = props.onSessionChange;
  useLayoutEffect(() => {
    const session = { ...creator.state, pendingInvitee: attendee, pendingDuration: typedDuration };
    onSessionChange?.(session);
  }, [creator.state, attendee, typedDuration, onSessionChange]);
  const durationFields = typedDuration ?? asFields(duration);
  const setDurationField = (key: DurationUnit, value: string): void => {
    const next = { ...durationFields, [key]: value };
    setTypedDuration(next);
    const minutes = durationPartsToMinutes(next);
    // Boxes that don't add up to a duration leave the event alone; they keep showing what was
    // typed until they do, or until the field is handed back.
    if (minutes !== null) dispatch({ type: 'set-duration', minutes });
  };

  const formElement = useRef<HTMLFormElement>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  useEffect(() => {
    if (failedAttempts === 0) return;
    // Optional groups have rendered open by this point. Scope the lookup to this creator.
    const error = formElement.current?.querySelector<HTMLElement>('.ec-problem');
    const field = error?.closest('.ec-field');
    const target = field?.querySelector<HTMLElement>('input, select, textarea') ?? error;
    target?.focus({ preventScroll: true });
    target?.scrollIntoView?.({ block: 'center' });
  }, [failedAttempts]);

  const requestExport = (): ReturnType<typeof creator.requestExport> => {
    const result = creator.requestExport();
    if (!result.ok) setFailedAttempts((count) => count + 1);
    return result;
  };

  const exportDraft = (): void => {
    const result = requestExport();
    if (!result.ok) return;
    props.onExport?.(result.export);
    (props.deliver ?? deliverExport)(result.export);
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (collecting) props.onAdd?.(draft);
    else exportDraft();
  };

  const addAttendee = (): void => {
    const email = attendee.trim();
    if (email === '') return;
    dispatch({ type: 'add-attendee', value: { email } });
    setAttendee('');
  };

  /**
   * The mark beside a label whose field must hold something before the event will export.
   *
   * Only five of the fourteen controls qualify, and they are the five that open pre-filled — so
   * this is less an instruction than a warning about what not to clear. It is `aria-hidden`
   * because the input carries `required`, which is what a screen reader announces; a spoken
   * "star" on top of that is noise.
   */
  const requiredMark = (
    <span className="ec-required" aria-hidden="true">
      *
    </span>
  );

  const problem = (field: string): React.JSX.Element | null => {
    const found = problemFor(field);
    return found === undefined ? null : (
      <p className="ec-problem" role="alert" tabIndex={-1}>
        {problemMessage(found, m)}
      </p>
    );
  };

  return (
    // ec-shell is the container query's subject; ec-root is what it sizes (see styles.ts).
    <div className="ec-shell">
      <div className="ec-root" data-preview={showPreview ? 'on' : 'off'}>
        <form ref={formElement} className="ec-fields" onSubmit={submit} noValidate>
          {/* Grouped the way a calendar app groups them — what it is, when it is, who is coming,
              what warns you, and the details — rather than fourteen fields in one column. The
              legends are real ones: a screen reader announces which group a field is in. */}
          <fieldset className="ec-group">
            <legend>{m.groupEvent}</legend>
            <div className="ec-field">
              <label htmlFor="ec-title">
                {m.title}
                {requiredMark}
              </label>
              <input
                id="ec-title"
                type="text"
                required
                maxLength={LIMITS.title}
                value={draft.title}
                placeholder={m.titlePlaceholder}
                onChange={(event) => dispatch({ type: 'set-title', value: event.target.value })}
              />
              {problem('title')}
            </div>

            <div className="ec-field">
              <label htmlFor="ec-location">{m.location}</label>
              <input
                id="ec-location"
                type="text"
                maxLength={LIMITS.location}
                value={draft.location ?? ''}
                onChange={(event) => dispatch({ type: 'set-location', value: event.target.value })}
              />
              {problem('location')}
            </div>
          </fieldset>

          <fieldset className="ec-group">
            <legend>{m.groupWhen}</legend>
            <label className="ec-inline ec-switch">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(event) => dispatch({ type: 'set-all-day', value: event.target.checked })}
              />
              {m.allDay}
            </label>

            {/* Each side of the event — its date, its time, and the zone that time is in —
                is one wrapping unit. Date and time stay on one compact line, with the searchable
                zone beneath. Keeping the zone inside the
                side it belongs to is the whole point: a lone zone select under both rows cannot
                say which time it applies to, and once the two sides can differ it has to. */}
            <div className="ec-row">
              <div className="ec-pair">
                <div className="ec-date-time">
                  <div className="ec-field">
                    <label htmlFor="ec-start-date">
                      {m.starts}
                      {requiredMark}
                    </label>
                    <input
                      id="ec-start-date"
                      type="date"
                      required
                      value={startDate}
                      onChange={(event) =>
                        dispatch({ type: 'set-start-date', value: event.target.value })
                      }
                    />
                  </div>
                  {!allDay && (
                    <div className="ec-field">
                      <label htmlFor="ec-start-time">
                        {m.startTime}
                        {requiredMark}
                      </label>
                      <div className="ec-time-control">
                        <input
                          id="ec-start-time"
                          type="time"
                          required
                          value={startTime}
                          onChange={(event) =>
                            dispatch({ type: 'set-start-time', value: event.target.value })
                          }
                        />
                        {startOffset !== undefined && (
                          <span className="ec-time-offset">{startOffset}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {!allDay && (
                  <div className="ec-field ec-zone-field">
                    <label htmlFor="ec-zone">{splitZones ? m.startTimeZone : m.timeZone}</label>
                    <TimeZonePicker
                      id="ec-zone"
                      selectedZone={creator.state.timeZone}
                      choices={zoneChoices}
                      locale={locale}
                      messages={m}
                      onChange={(value) => dispatch({ type: 'set-time-zone', value })}
                    />
                  </div>
                )}
              </div>
              <div className="ec-pair">
                <div className="ec-date-time">
                  <div className="ec-field">
                    <label htmlFor="ec-end-date">
                      {m.ends}
                      {requiredMark}
                    </label>
                    <input
                      id="ec-end-date"
                      type="date"
                      required
                      value={endDate}
                      onChange={(event) =>
                        dispatch({ type: 'set-end-date', value: event.target.value })
                      }
                    />
                  </div>
                  {!allDay && (
                    <div className="ec-field">
                      <label htmlFor="ec-end-time">
                        {m.endTime}
                        {requiredMark}
                      </label>
                      <div className="ec-time-control">
                        <input
                          id="ec-end-time"
                          type="time"
                          required
                          value={endTime}
                          onChange={(event) =>
                            dispatch({ type: 'set-end-time', value: event.target.value })
                          }
                        />
                        {endOffset !== undefined && (
                          <span className="ec-time-offset">{endOffset}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* The end's zone sits under the end, and only exists once the two differ. The
                    slot is left empty otherwise rather than filled with a duplicate of the start
                    zone, which would imply two independent controls where there is one. */}
                {!allDay && splitZones && (
                  <div className="ec-field ec-zone-field">
                    <label htmlFor="ec-end-zone">{m.endTimeZone}</label>
                    <TimeZonePicker
                      id="ec-end-zone"
                      selectedZone={endZone}
                      choices={zoneChoices}
                      locale={locale}
                      messages={m}
                      onChange={(value) => dispatch({ type: 'set-end-time-zone', value })}
                    />
                  </div>
                )}
              </div>
            </div>
            {problem('start')}
            {problem('end')}

            {/* The other way round from the two dates above: say how long it lasts and the end
                follows. Most events are known as "half an hour", not as a time to stop, and the
                two stay in step either way — editing the end rewrites these.

                A real fieldset, because three boxes that only mean something together should be
                announced as one thing; the legend is the label the "Duration" heading would be. */}
            <fieldset className="ec-duration">
              <legend>{m.duration}</legend>
              <div className="ec-inline">
                {DURATION_UNITS.filter((unit) => !allDay || unit.key === 'days').map((unit) => (
                  <div className="ec-field ec-duration-part" key={unit.key}>
                    <label htmlFor={`ec-duration-${unit.key}`}>{m[unit.message]}</label>
                    <input
                      id={`ec-duration-${unit.key}`}
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={durationFields[unit.key]}
                      onChange={(event) => setDurationField(unit.key, event.target.value)}
                      // Leaving settles the boxes on the draft's own split, which is where the
                      // carry becomes visible: 26 in Hours comes back as 1 day, 2 hours.
                      onBlur={() => setTypedDuration(null)}
                    />
                  </div>
                ))}
              </div>
            </fieldset>

            {!allDay && (
              <>
                <p className="ec-hint">
                  {creator.state.timeZone === undefined
                    ? m.hintFloating
                    : splitZones
                      ? m.hintSplitZones
                      : `${fill(m.hintEveryoneSees, {
                          place: timeZonePlaceLabel(creator.state.timeZone, locale),
                        })}${creator.state.timeZone === browserZone ? m.hintYourTimeZone : ''}.`}
                </p>

                {/* DTSTART and DTEND each carry their own TZID, so an event may legitimately end
                    in a different zone from the one it started in — a flight is the whole reason
                    the parameter is per-property. Behind a checkbox because it is wrong for
                    almost every other event, and only offered once a start zone exists: without
                    one the times are floating, and there is nothing for the end to differ from. */}
                {creator.state.timeZone !== undefined && draft.end !== undefined && (
                  <label className="ec-inline ec-switch">
                    <input
                      type="checkbox"
                      checked={splitZones}
                      onChange={(event) => {
                        setSplitZones(event.target.checked);
                        if (!event.target.checked)
                          dispatch({ type: 'set-end-time-zone', value: creator.state.timeZone });
                      }}
                    />
                    {m.endsInAnotherZone}
                  </label>
                )}
              </>
            )}

            <div className="ec-field">
              <label htmlFor="ec-repeat">{m.repeat}</label>
              <select
                id="ec-repeat"
                value={repeatPreset}
                onChange={(event) => {
                  const id = event.target.value;
                  setCustomRepeat(id === 'custom');
                  if (id === 'never') return setRecurrence(undefined);
                  const preset = REPEAT_PRESETS.find((entry) => entry.id === id);
                  if (preset === undefined || !('frequency' in preset)) {
                    // Custom starts from whatever is already set, so switching to it never
                    // silently rewrites a rule the user built.
                    return setRecurrence(recurrence ?? { frequency: 'weekly' });
                  }
                  setRecurrence({
                    ...withoutEnd(recurrence ?? { frequency: preset.frequency }),
                    frequency: preset.frequency,
                    ...(preset.interval === 1 ? {} : { interval: preset.interval }),
                    ...(recurrence?.count === undefined ? {} : { count: recurrence.count }),
                    ...(recurrence?.until === undefined ? {} : { until: recurrence.until }),
                  });
                }}
              >
                {REPEAT_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {m[preset.message]}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom is the only place a frequency and an interval need their own controls; the
              presets above cover the cases that would otherwise send someone here. */}
            {recurrence !== undefined && repeatPreset === 'custom' && (
              <div className="ec-row">
                <div className="ec-field">
                  <label htmlFor="ec-repeat-frequency">{m.frequency}</label>
                  <select
                    id="ec-repeat-frequency"
                    value={recurrence.frequency}
                    onChange={(event) =>
                      setRecurrence({
                        ...recurrence,
                        frequency: event.target.value as RecurrenceFrequency,
                      })
                    }
                  >
                    {FREQUENCIES.map((frequency) => (
                      <option key={frequency} value={frequency}>
                        {FREQUENCY_MESSAGE[frequency](m)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ec-field">
                  <label htmlFor="ec-repeat-interval">{m.every}</label>
                  <div className="ec-inline">
                    <input
                      id="ec-repeat-interval"
                      type="number"
                      min={1}
                      max={LIMITS.recurrenceInterval}
                      inputMode="numeric"
                      value={recurrence.interval ?? 1}
                      onChange={(event) =>
                        setRecurrence({
                          ...recurrence,
                          // Empty or nonsense means 1 rather than an error: the field has a
                          // sensible reading of "blank", and RRULE omits INTERVAL=1 anyway.
                          interval: Math.max(1, Math.trunc(Number(event.target.value) || 1)),
                        })
                      }
                    />
                    <span className="ec-unit">
                      {intervalUnit(recurrence.frequency, recurrence.interval ?? 1, m, locale)}
                    </span>
                  </div>
                  {problem('recurrence.interval')}
                </div>
              </div>
            )}

            {recurrence !== undefined && (
              <>
                <div className="ec-field">
                  {/* "End repeat" is Apple's wording, and it avoids colliding with the event's own
                    Ends field a few rows up — two controls labelled "Ends" is a real ambiguity,
                    for a screen reader reading them in sequence and for everyone else. */}
                  <label htmlFor="ec-repeat-ends">{m.endRepeat}</label>
                  <select
                    id="ec-repeat-ends"
                    value={repeatEnd}
                    onChange={(event) => {
                      const next = event.target.value as RepeatEnd;
                      // COUNT and UNTIL are mutually exclusive in RFC 5545, and the validator
                      // rejects both together — so switching modes drops the other one here
                      // rather than leaving the form in a state it can't export.
                      if (next === 'never') setRecurrence(withoutEnd(recurrence));
                      else if (next === 'count')
                        setRecurrence({ ...withoutEnd(recurrence), count: 10 });
                      else setRecurrence({ ...withoutEnd(recurrence), until: startDate });
                    }}
                  >
                    <option value="never">{m.endRepeatNever}</option>
                    <option value="count">{m.endRepeatCount}</option>
                    <option value="until">{m.endRepeatUntil}</option>
                  </select>
                </div>

                {repeatEnd === 'count' && (
                  <div className="ec-field">
                    <label htmlFor="ec-repeat-count">{m.numberOfTimes}</label>
                    <input
                      id="ec-repeat-count"
                      type="number"
                      min={1}
                      max={LIMITS.recurrenceCount}
                      inputMode="numeric"
                      value={recurrence.count ?? 1}
                      onChange={(event) =>
                        setRecurrence({
                          ...withoutEnd(recurrence),
                          count: Math.max(1, Math.trunc(Number(event.target.value) || 1)),
                        })
                      }
                    />
                    {problem('recurrence.count')}
                  </div>
                )}

                {repeatEnd === 'until' && (
                  <div className="ec-field">
                    <label htmlFor="ec-repeat-until">{m.lastDate}</label>
                    <input
                      id="ec-repeat-until"
                      type="date"
                      value={recurrence.until ?? ''}
                      onChange={(event) =>
                        setRecurrence({ ...withoutEnd(recurrence), until: event.target.value })
                      }
                    />
                    {problem('recurrence.until')}
                  </div>
                )}
                {problem('recurrence')}
                <p className="ec-hint">{repeatSummary(recurrence, m, locale)}</p>
              </>
            )}
          </fieldset>

          <Optional
            {...fold(
              'invitees',
              m.groupInvitees,
              tally(guests.length > 0 && plural(m.tallyInvited, guests.length, locale)),
              holdsProblem('attendees'),
            )}
          >
            <div className="ec-field">
              <label htmlFor="ec-attendee">{m.addInvitee}</label>
              <div className="ec-inline">
                <input
                  id="ec-attendee"
                  type="email"
                  value={attendee}
                  placeholder={m.inviteePlaceholder}
                  onChange={(event) => setAttendee(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addAttendee();
                    }
                  }}
                />
                <button type="button" className="ec-chip" onClick={addAttendee}>
                  {m.add}
                </button>
              </div>
              {guests.map((guest, index) => (
                <div className="ec-attendee" key={`${guest.email}-${index}`}>
                  <span>{guest.email}</span>
                  <button
                    type="button"
                    className="ec-remove"
                    aria-label={fill(m.removeInvitee, { email: guest.email })}
                    onClick={() => dispatch({ type: 'remove-attendee', index })}
                  >
                    {m.remove}
                  </button>
                </div>
              ))}
              {guests.map((_, index) => problem(`attendees[${index}].email`))}
              {problem('attendees')}
            </div>
          </Optional>

          <Optional
            {...fold(
              'alerts',
              m.groupAlerts,
              tally(
                alerts.length > 0 && plural(m.tallyAlerts, alerts.length, locale),
                draft.showAs === 'free' && m.tallyShownAsFree,
              ),
              holdsProblem('reminders'),
            )}
          >
            <div className="ec-row ec-row-tight">
              {alertRows.map((minutes, index) => (
                <div className="ec-field" key={`alert-${index}`}>
                  <label htmlFor={`ec-alert-${index}`}>{alertRowLabel(index, m)}</label>
                  <select
                    id={`ec-alert-${index}`}
                    value={minutes ?? ''}
                    onChange={(event) =>
                      dispatch({
                        type: 'set-reminder',
                        index,
                        minutes: event.target.value === '' ? undefined : Number(event.target.value),
                      })
                    }
                  >
                    <option value="">{m.alertNone}</option>
                    {ALERT_CHOICES.map((choice) => (
                      <option key={choice} value={choice}>
                        {alertLabel(choice, m, locale)}
                      </option>
                    ))}
                  </select>
                  {problem(`remindersMinutesBefore[${index}]`)}
                </div>
              ))}

              <div className="ec-field">
                {/* TRANSP, which decides whether the event blocks your free/busy or merely sits in
                the calendar. Apple and Outlook both call it "Show as". */}
                <label htmlFor="ec-show-as">{m.showAs}</label>
                <select
                  id="ec-show-as"
                  value={draft.showAs ?? 'busy'}
                  onChange={(event) =>
                    dispatch({ type: 'set-show-as', value: event.target.value as EventShowAs })
                  }
                >
                  <option value="busy">{m.showAsBusy}</option>
                  <option value="free">{m.showAsFree}</option>
                </select>
              </div>
            </div>
            {/* "Too many reminders" belongs to the group, not to any one of its selects. */}
            {problem('remindersMinutesBefore')}
          </Optional>

          <Optional
            {...fold(
              'details',
              m.groupDetails,
              tally(
                (draft.url ?? '') !== '' && m.tallyLink,
                (draft.description ?? '') !== '' && m.tallyNotes,
              ),
              holdsProblem('url', 'description'),
            )}
          >
            <div className="ec-field">
              <label htmlFor="ec-url">{m.url}</label>
              <input
                id="ec-url"
                type="url"
                maxLength={LIMITS.url}
                inputMode="url"
                value={draft.url ?? ''}
                placeholder="https://…"
                onChange={(event) => dispatch({ type: 'set-url', value: event.target.value })}
              />
              {problem('url')}
            </div>

            <div className="ec-field">
              <label htmlFor="ec-description">{m.notes}</label>
              <textarea
                id="ec-description"
                maxLength={LIMITS.description}
                value={draft.description ?? ''}
                onChange={(event) =>
                  dispatch({ type: 'set-description', value: event.target.value })
                }
              />
              {problem('description')}
            </div>
          </Optional>

          {collecting && (
            <div className="ec-collect-action">
              <div>
                <strong>{draft.title.trim() || m.recapUntitled}</strong>
                <p className="ec-hint">{eventRecap(draft, m, locale, false)}</p>
              </div>
              <button type="submit" className="ec-button">
                {props.addLabel ?? m.addToList}
              </button>
              {props.onCancelEditing && (
                <button
                  type="button"
                  className="ec-button ec-secondary"
                  onClick={props.onCancelEditing}
                >
                  {props.cancelEditingLabel ?? 'Cancel editing'}
                </button>
              )}
            </div>
          )}
          {!props.onCancelEditing && (
            <ExportContainer className="ec-export-options">
              {collecting && (
                <summary>
                  {m.downloadEvent} · {m.copyText}
                </summary>
              )}
              <fieldset className="ec-group">
                <legend>{m.groupDownload}</legend>

                {/* The whole event in one line, right where the decision to download is made. Every
                field is plausible on its own; it is the combination people get wrong. */}
                <div className="ec-recap">
                  <strong>{draft.title.trim() || m.recapUntitled}</strong>
                  <p>{eventRecap(draft, m, locale, false)}</p>
                  {draft.start.kind === 'dateTime' && !draft.start.timeZone && (
                    <p>
                      {m.timeZone}: {m.recapNoZone}
                    </p>
                  )}
                  {previewDuration !== null && previewDuration >= 0 && (
                    <p>
                      {m.duration}:{' '}
                      {new Intl.NumberFormat(locale, {
                        style: 'unit',
                        unit: draft.start.kind === 'date' ? 'day' : 'minute',
                        unitDisplay: 'long',
                      }).format(
                        draft.start.kind === 'date' ? previewDuration / 1440 : previewDuration,
                      )}
                    </p>
                  )}
                </div>

                {/* The key sits here rather than above the first field: the mark only matters at the
                moment of download, and a form that opens with a note about asterisks reads as
                more work than it is. */}
                <p className="ec-hint">
                  <span className="ec-required" aria-hidden="true">
                    *
                  </span>{' '}
                  {m.requiredHint}
                </p>

                {creator.problems.length > 0 && (
                  <ul className="ec-error-summary">
                    {creator.problems.map((issue, index) => (
                      <li key={index}>{problemMessage(issue, m)}</li>
                    ))}
                  </ul>
                )}
                <div className="ec-actions">
                  <button
                    type={collecting ? 'button' : 'submit'}
                    onClick={collecting ? exportDraft : undefined}
                    className="ec-button"
                  >
                    {m.downloadEvent}
                  </button>
                  {props.onAdd !== undefined && !collecting && (
                    <button
                      type="button"
                      className="ec-button ec-secondary"
                      onClick={() => props.onAdd?.(draft)}
                    >
                      {props.addLabel ?? m.addToList}
                    </button>
                  )}
                </div>
                {props.downloadHelp && <p className="ec-hint">{props.downloadHelp}</p>}
                <SettingsContainer className="ec-export-settings">
                  {props.simpleExport && (
                    <summary>
                      {m.downloadAs}:{' '}
                      {
                        creator.formats.find((format) => format.id === creator.state.formatId)
                          ?.label
                      }
                    </summary>
                  )}
                  <div className="ec-field">
                    <label htmlFor="ec-format">{m.downloadAs}</label>
                    <select
                      id="ec-format"
                      value={creator.state.formatId}
                      onChange={(event) =>
                        dispatch({ type: 'set-format', value: event.target.value as EventFormatId })
                      }
                    >
                      {creator.formats.map((format) => (
                        <option key={format.id} value={format.id}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="ec-button ec-secondary"
                    onClick={async () => {
                      const result = requestExport();
                      if (!result.ok) return;
                      setCopied(await copyExport(result.export));
                    }}
                  >
                    {copied ? m.copied : m.copyText}
                  </button>
                </SettingsContainer>
              </fieldset>
            </ExportContainer>
          )}
        </form>

        {!props.onCancelEditing && (
          <aside className="ec-side">
            {props.propose !== undefined && (
              <DescribeEvent
                propose={props.propose}
                messages={m}
                onDraft={(value) => {
                  dispatch({ type: 'load', value });
                  // The folds were choices about the event that was here before this one.
                  setFolds({});
                }}
              />
            )}
            <div className="ec-panel">
              <h2>{m.addStraightToCalendar}</h2>
              <div className="ec-links">
                {creator.links.map((link) => (
                  <a key={link.provider} href={link.url} target="_blank" rel="noreferrer noopener">
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
            {showPreview && (
              <details className="ec-panel ec-technical-preview">
                <summary>
                  {fill(m.previewHeading, {
                    format:
                      creator.formats.find((format) => format.id === creator.state.formatId)
                        ?.label ?? '',
                  })}
                </summary>
                <pre className="ec-preview">{creator.preview ?? m.previewEmpty}</pre>
              </details>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
